import { afterRotate, getPointsAfterRotate } from './index';
type IPoint = { x: number, y: number };
type IFrame = { width: number, height: number, left: number, top: number};

interface IPointsAfterWidthOrHeightChangeProps {
    originalFrame: IFrame,  // 原始图形位置大小
    rotate: number, // 顺时针旋转的角度
    widthOffset?: number,   // 宽度变化值
    heightOffset?: number,  // 高度变化值
    isLeftChange?: boolean, // 是否为元素翻转后且左侧伸缩
    isTopChange?: boolean,    // 是否为元素翻转后且上侧伸缩
}

const NO_CHANGE_VAL = 0;
const LEFT_TOP_INDEX = 0;   // 左上角定点索引
const RIGHT_TOP_INDEX = 1;  // 右上角定点序号
const RIGHT_BOTTOM_INDEX = 2;   // 右下角定点索引
const LEFT_BOTTOM_INDEX = 3;   // 左下角定点索引

/**
 * 返回旋转的矩形宽度或者高度改变后新的矩形四个点的坐标
 * @param {IPointsAfterWidthOrHeightChangeProps} props
 * @returns
 */
export function getPointsAfterWidthOrHeightChange(props: IPointsAfterWidthOrHeightChangeProps): IPoint[] {
    const { originalFrame, rotate, widthOffset, heightOffset, isLeftChange, isTopChange } = props;
    const rotatePoints = getPointsAfterRotate(originalFrame, rotate);
    const oldCenter = {
        x: (originalFrame.width / 2) + originalFrame.left,
        y: (originalFrame.height / 2) + originalFrame.top,
    };
    const centerPoints: IPoint[] = [];
    if (typeof widthOffset !== 'undefined' && widthOffset !== NO_CHANGE_VAL) {
        let realWidthOffset: number;
        if (isLeftChange) {
            realWidthOffset = -widthOffset;
            rotatePoints[LEFT_TOP_INDEX] = getNewPointOfLine(rotatePoints[LEFT_TOP_INDEX], rotate, realWidthOffset);
            rotatePoints[LEFT_BOTTOM_INDEX] = getNewPointOfLine(
                rotatePoints[LEFT_BOTTOM_INDEX], rotate,
                realWidthOffset,
            );
        } else {
            realWidthOffset = widthOffset;
            rotatePoints[RIGHT_TOP_INDEX] = getNewPointOfLine(rotatePoints[RIGHT_TOP_INDEX], rotate, realWidthOffset);
            rotatePoints[RIGHT_BOTTOM_INDEX] = getNewPointOfLine(
                rotatePoints[RIGHT_BOTTOM_INDEX], rotate,
                realWidthOffset,
            );
        }
        centerPoints.push(getNewPointOfLine(oldCenter, rotate, realWidthOffset / 2));
    }
    if (typeof heightOffset !== 'undefined' && heightOffset !== NO_CHANGE_VAL) {
        let realHeightOffset: number;
        const realRotate = rotate + 90;
        if (isTopChange) {
            realHeightOffset = -heightOffset;
            rotatePoints[LEFT_TOP_INDEX] = getNewPointOfLine(
                rotatePoints[LEFT_TOP_INDEX], realRotate,
                realHeightOffset,
            );
            rotatePoints[RIGHT_TOP_INDEX] = getNewPointOfLine(
                rotatePoints[RIGHT_TOP_INDEX], realRotate,
                realHeightOffset,
            );
        } else {
            realHeightOffset = heightOffset;
            rotatePoints[RIGHT_BOTTOM_INDEX] = getNewPointOfLine(
                rotatePoints[RIGHT_BOTTOM_INDEX], realRotate,
                realHeightOffset,
            );
            rotatePoints[LEFT_BOTTOM_INDEX] = getNewPointOfLine(
                rotatePoints[LEFT_BOTTOM_INDEX], realRotate,
                realHeightOffset,
            );
        }
        centerPoints.push(getNewPointOfLine(oldCenter, realRotate, realHeightOffset / 2));
    }
    if (centerPoints.length === 0) return rotatePoints;
    let [newCenter] = centerPoints;
    if (centerPoints.length > 1) {
        newCenter = {
            x: centerPoints[0].x + centerPoints[1].x - oldCenter.x,
            y: centerPoints[0].y + centerPoints[1].y - oldCenter.y,
        };
    }
    const points: IPoint[] = [];
    rotatePoints.forEach((point) => {
        points.push(afterRotate(point, -rotate, newCenter.x, newCenter.y));
    });
    return points;
}

/**
 * 已知某一点坐标、线段长度和旋转角度，得到另一点坐标
 * @param oldPoint 已知的点坐标
 * @param {number} rotate 旋转角度
 * @param {number} lineLength 线段长度
 * @returns
*/
function getNewPointOfLine(oldPoint: IPoint, rotate: number, lineLength: number): IPoint {
    const radian = rotate * Math.PI / 180;
    const dx = Math.cos(radian) * lineLength;
    const dy = Math.sin(radian) * lineLength;
    return {
        x: oldPoint.x + dx,
        y: oldPoint.y + dy,
    };
}


/**
 * 等比例缩减宽高数值
 * 当前应用场景：
 * 渲染引擎在缩放页面时渲染图形会出现半像素问题，导致与画布同等大小的图形遮不住画布，右下侧偶尔会出现白边,暂时缩小一点背景画布
 * @param width 原宽度
 * @param height 原高度
 * @param reduceSize 缩减的数值
 */
export const reduceWHbyRatio = (
    width: number,
    height: number,
    reduceSize = 0,
): { newWidth: number, newHeight: number } => {
    if (!width || !height) {
        return { newWidth: width, newHeight: height };
    }
    const ratio = width / height;
    const newHeight = height - reduceSize;
    const newWidth = newHeight * ratio;
    return { newWidth, newHeight };
};
